/**
 * ClientMediaPanel — Iter 64
 *
 * Reusable panel to manage the client's business logo + photo gallery.
 * Used from two places with identical behavior:
 *   • Admin  → /admin/clients/:id  (admin editing on behalf of the client)
 *   • Client → /client/dashboard   (self-service on their own profile)
 *
 * Backend endpoints (all admin OR self):
 *   GET    /api/users/:id/media
 *   POST   /api/users/:id/logo      (multipart, file)
 *   DELETE /api/users/:id/logo
 *   POST   /api/users/:id/gallery   (multipart, file, one per request)
 *   DELETE /api/users/:id/gallery   (body: { url })
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, ImagePlus, Loader2, X } from 'lucide-react';
import api, { apiErrorMessage } from '@/lib/api';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(u) {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${BACKEND_URL}${u}`;
}

export default function ClientMediaPanel({ userId, editable = true, compact = false }) {
    const [media, setMedia] = useState({ logo_url: null, business_photos: [] });
    const [loading, setLoading] = useState(true);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [error, setError] = useState('');
    const logoInputRef = useRef(null);
    const galleryInputRef = useRef(null);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await api.get(`/users/${userId}/media`);
            setMedia(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo cargar la identidad visual'));
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    const uploadLogo = async (file) => {
        if (!file) return;
        setError('');
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post(`/users/${userId}/logo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setMedia(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo subir el logo'));
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) logoInputRef.current.value = '';
        }
    };

    const removeLogo = async () => {
        if (!window.confirm('¿Eliminar el logo actual?')) return;
        setError('');
        try {
            const r = await api.delete(`/users/${userId}/logo`);
            setMedia(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo eliminar el logo'));
        }
    };

    const addPhoto = async (file) => {
        if (!file) return;
        setError('');
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post(`/users/${userId}/gallery`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setMedia(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo subir la foto'));
        } finally {
            setUploadingPhoto(false);
            if (galleryInputRef.current) galleryInputRef.current.value = '';
        }
    };

    const removePhoto = async (url) => {
        if (!window.confirm('¿Eliminar esta foto de la galería?')) return;
        setError('');
        try {
            const r = await api.delete(`/users/${userId}/gallery`, { data: { url } });
            setMedia(r.data);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo eliminar la foto'));
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center gap-2 text-sm text-gray-500" data-testid="client-media-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando identidad visual…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="client-media-panel">
            {error && (
                <div className="p-3 text-sm bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300" data-testid="client-media-error">
                    {error}
                </div>
            )}

            {/* --------- Logo --------- */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider font-bold text-gray-500">Logotipo del negocio</div>
                    <div className="text-[10px] text-gray-400">JPG · PNG · WEBP · máx. 5 MB</div>
                </div>
                <div className="flex items-center gap-4">
                    <div className={`${compact ? 'w-16 h-16' : 'w-24 h-24'} shrink-0 flex items-center justify-center border-2 border-dashed`}
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                        data-testid="client-logo-preview">
                        {media.logo_url ? (
                            <img src={resolveUrl(media.logo_url)} alt="Logo"
                                className="w-full h-full object-contain"
                                data-testid="client-logo-preview-img" />
                        ) : (
                            <ImagePlus className="w-6 h-6 text-gray-400" />
                        )}
                    </div>
                    {editable && (
                        <div className="flex flex-col gap-2">
                            <input ref={logoInputRef} type="file" accept={ACCEPT}
                                onChange={(e) => uploadLogo(e.target.files?.[0])}
                                className="hidden" data-testid="client-logo-file-input" />
                            <button type="button" onClick={() => logoInputRef.current?.click()}
                                disabled={uploadingLogo}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                                style={{ borderColor: 'var(--border)' }}
                                data-testid="client-logo-upload">
                                {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                {media.logo_url ? 'Reemplazar logo' : 'Subir logo'}
                            </button>
                            {media.logo_url && (
                                <button type="button" onClick={removeLogo}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-red-600 hover:bg-red-600 hover:text-white transition-colors border-2 border-red-600"
                                    data-testid="client-logo-delete">
                                    <Trash2 className="w-3.5 h-3.5" /> Eliminar logo
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {media.logo_uploaded_at && (
                    <div className="text-[10px] text-gray-400 mt-2" data-testid="client-logo-audit">
                        Última actualización: {new Date(media.logo_uploaded_at).toLocaleString('es-MX')}
                    </div>
                )}
            </div>

            {/* --------- Gallery --------- */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider font-bold text-gray-500">
                        Galería del negocio {media.business_photos?.length > 0 && `(${media.business_photos.length})`}
                    </div>
                    <div className="text-[10px] text-gray-400">JPG · PNG · WEBP · máx. 8 MB c/u</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {(media.business_photos || []).map((p, i) => (
                        <div key={p.url} className="relative group aspect-square border overflow-hidden"
                            style={{ borderColor: 'var(--border)' }}
                            data-testid={`client-gallery-item-${i}`}>
                            <img src={resolveUrl(p.url)} alt="" className="w-full h-full object-cover" />
                            {editable && (
                                <button type="button" onClick={() => removePhoto(p.url)}
                                    className="absolute top-1 right-1 w-7 h-7 flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                    title="Eliminar"
                                    data-testid={`client-gallery-delete-${i}`}>
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                    {editable && (
                        <>
                            <input ref={galleryInputRef} type="file" accept={ACCEPT}
                                onChange={(e) => addPhoto(e.target.files?.[0])}
                                className="hidden" data-testid="client-gallery-file-input" />
                            <button type="button" onClick={() => galleryInputRef.current?.click()}
                                disabled={uploadingPhoto}
                                className="aspect-square border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-black hover:border-black dark:hover:text-white dark:hover:border-white transition-colors disabled:opacity-50"
                                style={{ borderColor: 'var(--border)' }}
                                data-testid="client-gallery-upload">
                                {uploadingPhoto ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <ImagePlus className="w-5 h-5" />
                                        <span>Agregar foto</span>
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
                {(media.business_photos || []).length === 0 && !editable && (
                    <div className="text-sm text-gray-500 italic" data-testid="client-gallery-empty">
                        Sin fotos aún.
                    </div>
                )}
            </div>
        </div>
    );
}
